import { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import '../../fonts/fonts.css'
import graduationIcon from '../../images/icon-graduation.png'
import searchIcon from '../../images/search.svg'
import logoTgdEval       from '../../images/logo-tgd-eval.png'
import logoTgdMeteo      from '../../images/logo-tgd-meteo.png'
import logoTgdDeclivite from '../../images/logo-tgd-declivite.png'
import logoTgdRemc       from '../../images/logo-tgd-remc.png'
import logoTgdEco        from '../../images/logo-tgd-eco.png'
import logoTgdPasserelle from '../../images/logo-tgd-passerelle.png'
import logoTgdPostPermis from '../../images/logo-tgd-post-permis.png'
import {
  PageWrapper,
  PageHeader, PageIconBox, PageIconImg, PageTitle, PageSubtitle,
  FilterArea, FilterRow, FilterField, FilterFieldLabel, FilterSearchBox,
  FilterActionBtn, FilterResetBtn,
  TableArea, TableCard, EmptyRow,
  AgencyBlock, AgencyRow, ArrowBtn, AgencyNameText, AgencyStats, StatPill,
  FormationCardsWrapper, FormationCardsInner,
  FormationCard, FormationLogo, FormationLogoImg, FormationInfoBlock, FormationCategory, FormationName,
  FormationQtyBlock, FormationQtyLine, FormationSpacer,
} from './styledComponents'

const apiBase = process.env.REACT_APP_TGDAPI_SERVER || ''

const getSessionMeta = ({ category, title }) => {
  const t = (title || '').toLowerCase()
  if (category === 'Eval') return { color: '#6462FC', label: 'ÉVALUATION ET BILAN', logo: logoTgdEval }
  if (category === 'Hardware') return { color: '#00C8F8', label: 'MATÉRIEL', logo: logoTgdRemc }
  if (category === 'Conduite') {
    if (t.includes('météo') || t.includes('meteo')) return { color: '#3494FA', label: 'MÉTÉO', logo: logoTgdMeteo }
    if (t.includes('déclivité') || t.includes('declivite')) return { color: '#3494FA', label: 'DÉCLIVITÉ', logo: logoTgdDeclivite }
    if (t.includes('éco') || t.includes('eco')) return { color: '#3494FA', label: 'ÉCO-CONDUITE', logo: logoTgdEco }
    if (t.includes('post permis') || t.includes('post-permis')) return { color: '#3494FA', label: 'POST PERMIS', logo: logoTgdPostPermis }
    if (t.includes('passerelle')) return { color: '#3494FA', label: 'PASSERELLE', logo: logoTgdPasserelle }
    return { color: '#3494FA', label: 'CONDUITE', logo: logoTgdRemc }
  }
  return { color: '#6462FC', label: (category || 'FORMATION').toUpperCase(), logo: logoTgdRemc }
}

const AdminFormations = () => {
  const agencyToken  = useSelector(state => state.toJS().global.agencytoken) || ''
  const agencies     = useSelector(state => state.toJS().global.agencies || [])
  const ownAgencyName = useSelector(state => state.toJS().global.agencyName) || ''

  const [search,          setSearch]          = useState('')
  const [typeFilter,      setTypeFilter]      = useState('')
  const [expanded,        setExpanded]        = useState({})
  const [agencySessions,  setAgencySessions]  = useState({})
  const [loadingAgency,   setLoadingAgency]   = useState({})
  const [visibleAgencyIds, setVisibleAgencyIds] = useState(null)
  const [typeSearching,   setTypeSearching]   = useState(false)

  const resolveAgencyName = (agencyId) => {
    if (!agencyId || agencyId === 'self') return ownAgencyName || 'Mon agence'
    const ag = agencies.find(a => a.agencyId === agencyId || a.id?.toString() === agencyId?.toString())
    return ag ? (ag.agencyName || ag.agencyname || String(agencyId)) : String(agencyId)
  }

  const fetchAgencyData = async (agencyId) => {
    const [sessionsList, ordersList] = await Promise.all([
      fetch(`${apiBase}/sessions?agencyId=${agencyId}`, {
        headers: { 'Authorization': `Bearer ${agencyToken}` },
      })
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
        .then(data => Array.isArray(data) ? data : (data.sessions || [])),
      fetch(`${apiBase}/order?agencyId=${agencyId}`, {
        headers: { 'Authorization': `Bearer ${agencyToken}` },
      })
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
        .then(data => Array.isArray(data) ? data : (data.orders || [])),
    ])
    const orderMap = {}
    ordersList.forEach(o => { orderMap[o._id] = o })
    const formationMap = {}
    sessionsList.forEach(session => {
      const order = orderMap[session.orderId]
      const item  = order?.items?.[session.orderItemIndex]
      if (!item) return
      // Regroupé par produit, comme Mes formations
      const key = item.productId || item.name
      if (!formationMap[key]) {
        formationMap[key] = { key, formation: { title: item.name, category: item.category || 'Conduite' }, sessions: [] }
      }
      formationMap[key].sessions.push(session)
    })
    const formations = Object.values(formationMap).map(f => ({
      ...f,
      available: f.sessions.filter(s => !s.studentId).length,
      assigned:  f.sessions.filter(s => s.studentId && s.status !== 'completed').length,
      completed: f.sessions.filter(s => s.status === 'completed').length,
    }))
    return {
      formations,
      available: sessionsList.filter(s => !s.studentId).length,
      assigned:  sessionsList.filter(s => s.studentId && s.status !== 'completed').length,
      completed: sessionsList.filter(s => s.status === 'completed').length,
    }
  }

  const loadAgencySessions = async (agencyId) => {
    if (agencySessions[agencyId]) return
    setLoadingAgency(prev => ({ ...prev, [agencyId]: true }))
    try {
      const data = await fetchAgencyData(agencyId)
      setAgencySessions(prev => ({ ...prev, [agencyId]: data }))
    } catch (e) {
      console.error('Error loading agency sessions:', e)
    } finally {
      setLoadingAgency(prev => ({ ...prev, [agencyId]: false }))
    }
  }

  const grouped = useMemo(
    () => agencies.map(ag => ({ agencyId: ag.agencyId || ag.id, agencyName: ag.agencyName || ag.agencyname || 'Unknown' })),
    [agencies]
  )

  const handleSearch = async () => {
    const nameQ = search.trim().toLowerCase()
    const agencyList = nameQ
      ? grouped.filter(ag => resolveAgencyName(ag.agencyId).toLowerCase().includes(nameQ))
      : grouped

    const typeQ = typeFilter.trim().toLowerCase()
    if (!typeQ) {
      const matching = new Set(agencyList.map(ag => String(ag.agencyId)))
      setVisibleAgencyIds(matching)
      setExpanded(Object.fromEntries([...matching].map(id => [id, true])))
      agencyList.forEach(ag => loadAgencySessions(ag.agencyId))
      return
    }

    setTypeSearching(true)
    const matching = new Set()
    const newSessions = {}

    await Promise.allSettled(
      agencyList.map(async (ag) => {
        if (!ag.agencyId) return
        try {
          const data = agencySessions[ag.agencyId] || await fetchAgencyData(ag.agencyId)
          if (!agencySessions[ag.agencyId]) newSessions[ag.agencyId] = data
          const hasMatch = data.formations.some(f =>
            (f.formation.category || '').toLowerCase().includes(typeQ) ||
            (f.formation.title    || '').toLowerCase().includes(typeQ)
          )
          if (hasMatch) matching.add(String(ag.agencyId))
        } catch {
          // skip agency on fetch error
        }
      })
    )

    if (Object.keys(newSessions).length > 0) {
      setAgencySessions(prev => ({ ...prev, ...newSessions }))
    }
    setVisibleAgencyIds(matching)
    setExpanded(Object.fromEntries([...matching].map(id => [id, true])))
    setTypeSearching(false)
  }

  const resetSearch = () => {
    setSearch('')
    setTypeFilter('')
    setVisibleAgencyIds(null)
    setExpanded({})
  }

  const toggle = (agencyId) => {
    const wasOpen = !!expanded[agencyId]
    setExpanded(prev => ({ ...prev, [agencyId]: !wasOpen }))
    if (!wasOpen) loadAgencySessions(agencyId)
  }

  const filteredGrouped = useMemo(() => {
    if (visibleAgencyIds === null) return grouped
    return grouped.filter(ag => visibleAgencyIds.has(String(ag.agencyId)))
  }, [grouped, visibleAgencyIds])

  return (
    <PageWrapper>
      <PageHeader>
        <PageIconBox>
          <PageIconImg src={graduationIcon} alt='' />
        </PageIconBox>
        <div>
          <PageTitle>Formations</PageTitle>
          <PageSubtitle>Gestion de toutes les sessions par agence</PageSubtitle>
        </div>
      </PageHeader>

      <FilterArea>
        <FilterRow>
          <FilterField>
            <FilterFieldLabel>Agence</FilterFieldLabel>
            <FilterSearchBox>
              <img src={searchIcon} alt='' />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder='Chercher une agence…'
              />
            </FilterSearchBox>
          </FilterField>
          <FilterField>
            <FilterFieldLabel>Type de formation</FilterFieldLabel>
            <FilterSearchBox>
              <img src={searchIcon} alt='' />
              <input
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                placeholder='Ex : Conduite, Eval, Hardware…'
              />
            </FilterSearchBox>
          </FilterField>
          {(search.trim() || typeFilter.trim() || visibleAgencyIds !== null) && (
            <>
              <FilterActionBtn onClick={handleSearch} disabled={typeSearching}>
                <img src={searchIcon} alt='' />
                {typeSearching ? 'Recherche…' : 'Rechercher'}
              </FilterActionBtn>
              <FilterResetBtn onClick={resetSearch}>✕ Réinitialiser</FilterResetBtn>
            </>
          )}
        </FilterRow>
      </FilterArea>

      <TableArea>
        <TableCard>
          {filteredGrouped.length === 0 && (
            <EmptyRow>
              {typeSearching ? 'Recherche en cours…' : 'Aucune agence ne correspond à votre recherche.'}
            </EmptyRow>
          )}

          {filteredGrouped.map(ag => {
            const open            = !!expanded[ag.agencyId]
            const name            = resolveAgencyName(ag.agencyId)
            const agencyData      = agencySessions[ag.agencyId]
            const isLoadingAgency = loadingAgency[ag.agencyId]

            return (
              <AgencyBlock key={ag.agencyId}>
                <AgencyRow $open={open} onClick={() => toggle(ag.agencyId)}>
                  <ArrowBtn $open={open}>▶</ArrowBtn>
                  <AgencyNameText>{name}</AgencyNameText>
                  <AgencyStats>
                    {isLoadingAgency && <StatPill style={{ opacity: 0.5 }}>Chargement…</StatPill>}
                    {agencyData && !isLoadingAgency && (
                      <>
                        <StatPill>{agencyData.available} disponible{agencyData.available > 1 ? 's' : ''}</StatPill>
                        <StatPill $type='price'>{agencyData.assigned} attribuée{agencyData.assigned > 1 ? 's' : ''}</StatPill>
                        <StatPill $type='completed'>{agencyData.completed} terminée{agencyData.completed > 1 ? 's' : ''}</StatPill>
                      </>
                    )}
                  </AgencyStats>
                </AgencyRow>

                <FormationCardsWrapper $open={open}>
                  <FormationCardsInner>
                    {isLoadingAgency && <EmptyRow style={{ padding: '1.25rem' }}>Chargement des formations…</EmptyRow>}
                    {!isLoadingAgency && agencyData && (() => {
                      const visibleFormations = typeFilter.trim()
                        ? agencyData.formations.filter(f =>
                            (f.formation.category || '').toLowerCase().includes(typeFilter.trim().toLowerCase()) ||
                            (f.formation.title    || '').toLowerCase().includes(typeFilter.trim().toLowerCase())
                          )
                        : agencyData.formations
                      if (visibleFormations.length === 0) {
                        return (
                          <EmptyRow style={{ padding: '1.25rem' }}>
                            {agencyData.formations.length === 0 ? 'Aucune formation pour cette agence.' : 'Aucune formation ne correspond au filtre.'}
                          </EmptyRow>
                        )
                      }
                      return visibleFormations.map(formation => {
                        const { color, label, logo } = getSessionMeta(formation.formation)
                        return (
                          <FormationCard key={formation.key}>
                            <FormationLogo><FormationLogoImg src={logo} alt='' /></FormationLogo>
                            <FormationInfoBlock>
                              <FormationCategory color={color}>{label}</FormationCategory>
                              <FormationName>{formation.formation.title}</FormationName>
                            </FormationInfoBlock>
                            <FormationQtyBlock>
                              <FormationQtyLine>
                                <StatPill>{formation.available} disponible{formation.available > 1 ? 's' : ''}</StatPill>
                              </FormationQtyLine>
                              <FormationQtyLine>
                                <StatPill $type='price'>{formation.assigned} attribuée{formation.assigned > 1 ? 's' : ''}</StatPill>
                              </FormationQtyLine>
                              <FormationQtyLine>
                                <StatPill $type='completed'>{formation.completed} terminée{formation.completed > 1 ? 's' : ''}</StatPill>
                              </FormationQtyLine>
                            </FormationQtyBlock>
                            <FormationSpacer />
                          </FormationCard>
                        )
                      })
                    })()}
                  </FormationCardsInner>
                </FormationCardsWrapper>
              </AgencyBlock>
            )
          })}
        </TableCard>
      </TableArea>
    </PageWrapper>
  )
}

export default AdminFormations
